import { useNavigate } from 'react-router-dom'
import { useDecodeToken } from './useDecodeToken'
import { TokenStorageLocation } from './useDecodeToken'
import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useTokenClaims } from './useTokenClaims'
import { useIsAuthenticated } from '@azure/msal-react'
import { getProjectTemplate } from '../services/projectService'
import { getPostProjectSelectPath } from '../components/navigation/navigationUtils'

type AuthContextType = {
	updateToken: (newToken: string) => void
	token: string | null
	hasWpaToken: () => boolean
	removeWpaToken: () => void
	isAdmin: boolean
}

const ProjectContext = createContext<AuthContextType | null>(null)

const adminEmails = [
	'parham.shagerdmootab@brockgroup.com',
	'Josh.Dove@brockgroup.com',
	'Jayson.Wright@brockgroup.com',
	'Vanessa.Vargas@brockgroup.com',
]

const useAuthContext = (): AuthContextType => {
	const authContextState = useContext(ProjectContext)
	if (authContextState === null) {
		throw new Error(
			'AuthContext not found. Try wrapping a parent component with <AuthContext.Provider>.',
		)
	}
	return authContextState
}

type AuthProviderProps = {
	children?: React.ReactNode
}
const AuthProvider = ({ children }: AuthProviderProps) => {
	const isAuthenticated = useIsAuthenticated()
	const [token, setToken] = React.useState<string | null>(
		localStorage.getItem(TokenStorageLocation.WpaTokenStorage),
	)
	const decodedToken = useDecodeToken()
	const tokenClaims = useTokenClaims()

	const updateToken = (newToken: string) => {
		localStorage.setItem(TokenStorageLocation.WpaTokenStorage, newToken)
		setToken(newToken)
	}
	const navigate = useNavigate()

	const isAdmin = useMemo(() => {
		const email = decodedToken?.email
		if (!email) return false
		return adminEmails.some((adminEmail) => adminEmail.toLowerCase() === email.toLowerCase())
	}, [decodedToken?.email])

	const hasValidToken = () => {
		if (!decodedToken) return false
		const jsTime = new Date().getTime() / 1000
		const isFromIssuer = decodedToken?.iss === 'Brock' ? true : false
		const isNotExpired = decodedToken?.exp && decodedToken.exp >= jsTime ? true : false
		return isFromIssuer && isNotExpired
	}

	const firstRender = useRef(true)
	useEffect(() => {
		if (!hasValidToken() || !token || !isAuthenticated) return
		if (firstRender.current) {
			firstRender.current = false
			return
		}
		if (!tokenClaims.role.title) return

		;(async () => {
			const projectTemplate = await getProjectTemplate()
			navigate(
				getPostProjectSelectPath(
					tokenClaims.role.title,
					tokenClaims.projectId,
					projectTemplate,
				),
			)
		})()
	}, [token, isAuthenticated])
	return (
		<ProjectContext.Provider
			value={{
				updateToken,
				token,
				hasWpaToken: hasValidToken,
				isAdmin,
				removeWpaToken: () => {
					localStorage.removeItem(TokenStorageLocation.WpaTokenStorage)
					window.location.replace('/?deleted=true')
				},
			}}
		>
			{children}
		</ProjectContext.Provider>
	)
}

export const useRBAC = (roles?: ReadonlyArray<string>): boolean | null => {
	const decoded = useDecodeToken()
	return roles && decoded && decoded.role
		? roles.some((role) => decoded.role.includes(role))
		: false
}

export { AuthProvider, useAuthContext }
